import { createTheme, alpha } from "@mui/material/styles";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import {
  palette as p,
  radius,
  shadowsLight,
  shadowsDark,
  fontStack,
  layout,
} from "./tokens";

const buildPalette = (mode) => {
  const isDark = mode === "dark";

  if (isDark) {
    return {
      mode: "dark",
      primary: {
        main: p.blue[400],
        light: p.blue[300],
        dark: p.blue[600],
        contrastText: "#FFFFFF",
        soft: alpha(p.blue[400], 0.16),
      },
      secondary: { main: p.slate[400], contrastText: p.slate[900] },
      success: { main: p.green[400], soft: alpha(p.green[400], 0.16) },
      warning: { main: p.amber[400], soft: alpha(p.amber[400], 0.16) },
      error: { main: p.red[400], soft: alpha(p.red[400], 0.16) },
      info: { main: p.blue[400], soft: alpha(p.blue[400], 0.16) },
      violet: { main: p.violet[400], soft: alpha(p.violet[400], 0.16) },
      teal: { main: p.teal[400], soft: alpha(p.teal[400], 0.16) },
      nav: {
        bg: "#0E1731",
        fg: "#FFFFFF",
        muted: alpha("#FFFFFF", 0.62),
        section: alpha("#FFFFFF", 0.38),
        hover: alpha("#FFFFFF", 0.07),
        divider: alpha("#FFFFFF", 0.08),
      },
      tableHead: { bg: alpha(p.blue[400], 0.1), fg: p.blue[300] },
      background: {
        default: "#0B1220",
        paper: "#131B2E",
        elevated: "#1A2338",
        subtle: "#0F1728",
        field: "#182236",
      },
      text: {
        primary: p.slate[100],
        secondary: p.slate[400],
        disabled: p.slate[600],
      },
      divider: alpha("#FFFFFF", 0.08),
      border: {
        subtle: alpha("#FFFFFF", 0.06),
        default: alpha("#FFFFFF", 0.1),
        strong: alpha("#FFFFFF", 0.18),
      },
      action: {
        hover: alpha("#FFFFFF", 0.06),
        selected: alpha(p.blue[400], 0.14),
      },
    };
  }

  return {
    mode: "light",
    primary: {
      main: p.blue[600],
      light: p.blue[500],
      dark: p.blue[700],
      contrastText: "#FFFFFF",
      soft: p.blue[50],
    },
    secondary: { main: p.slate[600], contrastText: "#FFFFFF" },
    success: { main: p.green[600], soft: p.green[50] },
    warning: { main: p.amber[600], soft: p.amber[50] },
    error: { main: p.red[600], soft: p.red[50] },
    info: { main: p.blue[600], soft: p.blue[50] },
    violet: { main: p.violet[600], soft: p.violet[50] },
    teal: { main: p.teal[600], soft: p.teal[50] },
    nav: {
      bg: p.navy[900],
      fg: "#FFFFFF",
      muted: alpha("#FFFFFF", 0.66),
      section: alpha("#FFFFFF", 0.4),
      hover: alpha("#FFFFFF", 0.08),
      divider: alpha("#FFFFFF", 0.1),
    },
    tableHead: { bg: p.blue[50], fg: p.blue[800] },
    background: {
      // Was slate-50, which is indistinguishable from the white cards on it —
      // the whole app read as one white sheet. A cool tint lets cards lift.
      default: "#EDF1F8",
      paper: p.slate[0],
      elevated: p.slate[0],
      subtle: "#F1F4FA",
      // Input fill — between the white card and the tinted page.
      field: "#E3EAF5",
    },
    text: {
      primary: p.slate[900],
      secondary: p.slate[500],
      disabled: p.slate[400],
    },
    divider: p.slate[200],
    border: {
      subtle: p.slate[100],
      default: p.slate[200],
      strong: p.slate[300],
    },
    action: {
      hover: p.slate[100],
      selected: p.blue[50],
    },
  };
};

// Tight, modern scale. Headings get negative tracking, body stays neutral.
const typography = {
  fontFamily: fontStack,
  h1: { fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em" },
  h2: { fontSize: "1.625rem", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.02em" },
  h3: { fontSize: "1.375rem", fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.015em" },
  h4: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.01em" },
  h5: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.45 },
  subtitle1: { fontSize: "0.9375rem", fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.5 },
  body1: { fontSize: "0.9375rem", fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.55 },
  caption: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.5 },
  overline: {
    fontSize: "0.6875rem",
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  button: { fontSize: "0.875rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 },
};

export const buildTheme = (mode = "light") => {
  const isDark = mode === "dark";
  const shadow = isDark ? shadowsDark : shadowsLight;
  const pal = buildPalette(mode);

  const base = createTheme({
    palette: pal,
    typography,
    shape: { borderRadius: radius.md },
    spacing: 8,
    // MUI requires all 25 slots; map the soft scale across them.
    shadows: [
      "none",
      shadow.xs, shadow.xs,
      shadow.sm, shadow.sm, shadow.sm,
      shadow.md, shadow.md, shadow.md, shadow.md,
      shadow.lg, shadow.lg, shadow.lg, shadow.lg, shadow.lg,
      shadow.xl, shadow.xl, shadow.xl, shadow.xl, shadow.xl,
      shadow.xl, shadow.xl, shadow.xl, shadow.xl, shadow.xl,
    ],
  });

  return createTheme(base, {
    // Custom namespace so components reach tokens without re-importing them.
    tokens: { radius, layout, shadow, palette: p },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*, *::before, *::after": { boxSizing: "border-box" },
          // Anyone who has asked their OS to reduce motion gets none of it.
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
          "@keyframes shellFadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
          html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
          body: {
            backgroundColor: pal.background.default,
            color: pal.text.primary,
            // Stop iOS rubber-banding the page behind the app shell.
            overscrollBehaviorY: "none",
          },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? alpha("#FFFFFF", 0.14) : p.slate[300],
            borderRadius: radius.full,
            border: "3px solid " + pal.background.default,
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isDark ? alpha("#FFFFFF", 0.22) : p.slate[400],
          },
          "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: radius.lg },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            border: "1px solid " + pal.border.default,
            backgroundColor: pal.background.paper,
            boxShadow: shadow.xs,
            transition: "box-shadow .2s ease, border-color .2s ease, transform .2s ease",
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            // 40px on desktop, 44px on touch so tap targets stay reachable.
            minHeight: 40,
            padding: "8px 16px",
            transition:
              "background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease, transform .1s ease",
            // A press that moves confirms the tap landed, which matters most
            // on touch where there is no hover state to rely on.
            "&:active": { transform: "scale(0.97)" },
            "&.Mui-disabled": { transform: "none" },
            "@media (pointer: coarse)": { minHeight: 44 },
          },
          sizeSmall: { minHeight: 34, padding: "6px 12px", fontSize: "0.8125rem" },
          sizeLarge: { minHeight: 48, padding: "12px 22px", fontSize: "0.9375rem" },
          containedPrimary: {
            "&:hover": { backgroundColor: pal.primary.dark },
          },
          outlined: {
            borderColor: pal.border.strong,
            color: pal.text.primary,
            "&:hover": {
              borderColor: pal.border.strong,
              backgroundColor: pal.action.hover,
            },
          },
          text: {
            "&:hover": { backgroundColor: pal.action.hover },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            transition: "background-color .15s ease, color .15s ease, transform .1s ease",
            "&:active": { transform: "scale(0.92)" },
            "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            // Inputs sit on white cards, so a white input field was invisible
            // as a field — you only saw the outline. A light fill makes it
            // read as something you type into, without adding another border.
            backgroundColor: isDark ? alpha("#FFFFFF", 0.04) : pal.background.field,
            "&.Mui-disabled": { backgroundColor: isDark ? alpha("#FFFFFF", 0.02) : pal.background.subtle },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: pal.border.default },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: pal.border.strong },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: pal.primary.main,
            },
            // One key: a second "&.Mui-focused" below silently replaced the
            // first, so only whichever came last survived.
            "&.Mui-focused": {
              backgroundColor: isDark ? alpha("#FFFFFF", 0.06) : "#FFFFFF",
              boxShadow: "0 0 0 3px " + alpha(pal.primary.main, isDark ? 0.25 : 0.15),
            },
          },
          input: {
            // 16px on touch stops iOS Safari zooming the viewport on focus.
            "@media (pointer: coarse)": { fontSize: 16 },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: "0.9375rem", color: pal.text.secondary },
        },
      },

      MuiTextField: { defaultProps: { size: "small", variant: "outlined" } },

      MuiSelect: { defaultProps: { size: "small" } },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.sm, fontWeight: 500, fontSize: "0.75rem" },
          sizeSmall: { height: 22 },
          filled: { backgroundColor: pal.background.subtle, color: pal.text.primary },
          outlined: { borderColor: pal.border.default },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid " + pal.border.subtle,
            padding: "12px 16px",
            fontSize: "0.875rem",
          },
          head: {
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: pal.tableHead.fg,
            backgroundColor: pal.tableHead.bg,
            whiteSpace: "nowrap",
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background-color .15s ease",
            "&:last-child td": { borderBottom: "none" },
            "&.MuiTableRow-hover:hover": { backgroundColor: pal.action.hover },
          },
        },
      },

      MuiTableContainer: {
        styleOverrides: { root: { borderRadius: radius.lg } },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.xl,
            border: "1px solid " + pal.border.default,
            boxShadow: shadow.xl,
          },
          // Full-screen sheets must not keep rounded corners.
          paperFullScreen: { borderRadius: 0, border: "none" },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: "1.0625rem", fontWeight: 600, padding: "20px 24px 8px" },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
            border: "1px solid " + pal.border.default,
            boxShadow: shadow.lg,
            marginTop: 6,
          },
          list: { padding: 6 },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            fontSize: "0.875rem",
            minHeight: 38,
            transition: "background-color .15s ease, color .15s ease",
            "@media (pointer: coarse)": { minHeight: 44 },
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            transition: "background-color .15s ease, color .15s ease",
            "&.Mui-selected": {
              backgroundColor: pal.action.selected,
              "&:hover": { backgroundColor: pal.action.selected },
            },
          },
        },
      },

      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 0, color: "inherit" } },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? p.slate[700] : p.slate[800],
            fontSize: "0.75rem",
            borderRadius: radius.sm,
            padding: "6px 10px",
          },
          arrow: { color: isDark ? p.slate[700] : p.slate[800] },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: {
            backgroundColor: alpha(pal.background.paper, isDark ? 0.8 : 0.85),
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid " + pal.border.default,
            color: pal.text.primary,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: pal.background.paper, borderColor: pal.border.default },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: pal.border.subtle } } },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 42 },
          indicator: { height: 2, borderRadius: radius.full },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            minHeight: 42,
            "@media (pointer: coarse)": { minHeight: 46 },
          },
        },
      },

      MuiAlert: {
        styleOverrides: { root: { borderRadius: radius.md, fontSize: "0.875rem" } },
      },

      MuiTablePagination: {
        styleOverrides: {
          root: { borderTop: "1px solid " + pal.border.subtle },
          toolbar: { minHeight: 52, paddingLeft: 16 },
          selectLabel: { fontSize: "0.8125rem", color: pal.text.secondary },
          displayedRows: { fontSize: "0.8125rem", color: pal.text.secondary },
        },
      },

      MuiSkeleton: {
        styleOverrides: { root: { borderRadius: radius.sm } },
      },
    },
  });
};

// Default export keeps the existing `import theme from './theme'` working.
const theme = buildTheme("light");
export default theme;
